import { useCallback, useEffect, useMemo, useState } from "react";
import {
  checkStandardItemCatalogAvailable,
  fetchStandardItemCatalogPage,
  fetchStandardItemDistinct,
  type CatalogFilters,
  type StandardItemCatalogRow,
} from "../api/standardItemMasterApi";
import { useAppState } from "../context/AppStateContext";
import type { StandardResource } from "../types";
import { SearchableSelect } from "./SearchableSelect";

type Level = "mainGroup" | "group" | "category" | "subcategory";

const EMPTY_OPTS = [{ value: "", label: "— All / any —", rowKey: "none" }];

export function StandardItemCatalogPicker({ resource }: { resource: StandardResource }) {
  const { addStandardResourceItem } = useAppState();
  const [catalogOk, setCatalogOk] = useState<boolean | null>(null);
  const [mainGroup, setMainGroup] = useState("");
  const [group, setGroup] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [searchQ, setSearchQ] = useState("");

  const [optMain, setOptMain] = useState<string[]>([]);
  const [optGrp, setOptGrp] = useState<string[]>([]);
  const [optCat, setOptCat] = useState<string[]>([]);
  const [optSub, setOptSub] = useState<string[]>([]);

  const [items, setItems] = useState<StandardItemCatalogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selected, setSelected] = useState<StandardItemCatalogRow | null>(null);
  const [addQty, setAddQty] = useState("1");

  const filters: CatalogFilters = useMemo(
    () => ({
      mainGroup: mainGroup || undefined,
      group: group || undefined,
      category: category || undefined,
      subcategory: subcategory || undefined,
    }),
    [mainGroup, group, category, subcategory],
  );

  /** Item list loads only after user picks a main group (no full-catalog fetch on open). */
  const listReady = Boolean(mainGroup.trim());

  const existingCatalogIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of resource.items) {
      if (it.catalogStandardItemId) s.add(it.catalogStandardItemId);
    }
    return s;
  }, [resource.items]);

  useEffect(() => {
    let cancelled = false;
    checkStandardItemCatalogAvailable().then((ok) => {
      if (!cancelled) setCatalogOk(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDistinct = useCallback(
    async (level: Level, f: CatalogFilters, setter: (v: string[]) => void) => {
      try {
        const v = await fetchStandardItemDistinct(level, f);
        setter(v);
      } catch {
        setter([]);
      }
    },
    [],
  );

  useEffect(() => {
    if (!catalogOk) return;
    void loadDistinct("mainGroup", {}, setOptMain);
  }, [catalogOk, loadDistinct]);

  useEffect(() => {
    if (!catalogOk) return;
    setGroup("");
    setCategory("");
    setSubcategory("");
    if (!mainGroup) {
      setOptGrp([]);
      setOptCat([]);
      setOptSub([]);
      return;
    }
    void loadDistinct("group", { mainGroup }, setOptGrp);
  }, [catalogOk, mainGroup, loadDistinct]);

  useEffect(() => {
    if (!catalogOk || !mainGroup) return;
    setCategory("");
    setSubcategory("");
    if (!group) {
      setOptCat([]);
      setOptSub([]);
      return;
    }
    void loadDistinct("category", { mainGroup, group }, setOptCat);
  }, [catalogOk, mainGroup, group, loadDistinct]);

  useEffect(() => {
    if (!catalogOk || !mainGroup || !group) return;
    setSubcategory("");
    if (!category) {
      setOptSub([]);
      return;
    }
    void loadDistinct("subcategory", { mainGroup, group, category }, setOptSub);
  }, [catalogOk, mainGroup, group, category, loadDistinct]);

  const limit = 80;
  const loadItems = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (!catalogOk || !mainGroup.trim()) {
        if (!append) {
          setItems([]);
          setTotal(0);
          setOffset(0);
          setSelected(null);
        }
        return;
      }
      setLoading(true);
      setListError(null);
      try {
        const res = await fetchStandardItemCatalogPage({
          filters,
          q: searchQ.trim() || undefined,
          limit,
          offset: nextOffset,
        });
        setTotal(res.total);
        setOffset(nextOffset);
        setItems((prev) => (append ? [...prev, ...res.items] : res.items));
        if (!append) setSelected(null);
      } catch (e) {
        setListError(e instanceof Error ? e.message : "Could not load catalogue items.");
        if (!append) setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [catalogOk, mainGroup, filters, searchQ],
  );

  useEffect(() => {
    if (!catalogOk) return;
    if (!mainGroup.trim()) {
      setItems([]);
      setTotal(0);
      setOffset(0);
      setListError(null);
      setLoading(false);
      setSelected(null);
      return;
    }
    const t = window.setTimeout(() => void loadItems(0, false), 280);
    return () => window.clearTimeout(t);
  }, [catalogOk, mainGroup, filters, searchQ, loadItems]);

  const toSelectOpts = (vals: string[]) => [
    ...EMPTY_OPTS,
    ...vals.map((v) => ({ value: v, label: v, rowKey: v })),
  ];

  if (catalogOk === false || catalogOk === null) {
    return (
      <div className="standard-catalog-picker standard-catalog-picker--offline help-banner help-banner--info">
        <strong>Standard item catalogue</strong>
        <p style={{ margin: "0.35rem 0 0" }}>
          {catalogOk === null
            ? "Checking catalogue API…"
            : "Connect the API with MySQL and import `tabStandardItemMaster` to pick items by main group, group, category, and subcategory here."}
        </p>
      </div>
    );
  }

  return (
    <div className="standard-catalog-picker card card--gov">
      <div className="standard-catalog-picker__header">
        <h3 className="form-section-title">Add from standard item catalogue</h3>
        <p className="standard-catalog-picker__lead text-muted">
          Use main group (required), then optional filters and search. Pick a row in the table, set quantity, add to this
          resource.
        </p>
      </div>

      <div className="standard-catalog-picker__controls">
        <div className="field field-inline standard-catalog-picker__filter">
          <label htmlFor={`sim-main-${resource.id}`}>Main group</label>
          <SearchableSelect
            id={`sim-main-${resource.id}`}
            variant="dash"
            value={mainGroup}
            onChange={(v) => setMainGroup(v)}
            options={toSelectOpts(optMain)}
            placeholder="Select main group…"
          />
        </div>
        <div className="field field-inline standard-catalog-picker__filter">
          <label htmlFor={`sim-grp-${resource.id}`}>Group</label>
          <SearchableSelect
            id={`sim-grp-${resource.id}`}
            variant="dash"
            value={group}
            onChange={(v) => setGroup(v)}
            options={toSelectOpts(optGrp)}
            placeholder={mainGroup ? "— All —" : "Pick main group first"}
            disabled={!mainGroup}
          />
        </div>
        <div className="field field-inline standard-catalog-picker__filter">
          <label htmlFor={`sim-cat-${resource.id}`}>Category</label>
          <SearchableSelect
            id={`sim-cat-${resource.id}`}
            variant="dash"
            value={category}
            onChange={(v) => setCategory(v)}
            options={toSelectOpts(optCat)}
            placeholder={group ? "— All —" : "Pick group first"}
            disabled={!group}
          />
        </div>
        <div className="field field-inline standard-catalog-picker__filter">
          <label htmlFor={`sim-sub-${resource.id}`}>Subcategory</label>
          <SearchableSelect
            id={`sim-sub-${resource.id}`}
            variant="dash"
            value={subcategory}
            onChange={(v) => setSubcategory(v)}
            options={toSelectOpts(optSub)}
            placeholder={category ? "— All —" : "Pick category first"}
            disabled={!category}
          />
        </div>
        <div className="field field-inline standard-catalog-picker__search-field">
          <label htmlFor={`sim-q-${resource.id}`}>Search nomenclature / ID / family</label>
          <input
            id={`sim-q-${resource.id}`}
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="e.g. AAC or STD-"
            maxLength={200}
          />
        </div>
      </div>

      {!listReady ? (
        <p className="standard-catalog-picker__hint" role="status">
          Choose a <strong>main group</strong> first — the item table and actions appear here after you do.
        </p>
      ) : null}

      {listReady ? (
        <div className="standard-catalog-picker__results">
          {listError ? (
            <p className="standard-catalog-picker__error" role="alert">
              {listError}
            </p>
          ) : null}

          <div className="standard-catalog-picker__list-scroll">
            <div className="standard-catalog-picker__list">
              <div className="standard-catalog-picker__list-head" role="row">
                <span className="standard-catalog-picker__item-name" role="columnheader">
                  Item name
                </span>
                <span className="standard-catalog-picker__item-uom" role="columnheader">
                  UOM
                </span>
              </div>
              {loading && items.length === 0 ? (
                <div className="standard-catalog-picker__list-empty">Loading…</div>
              ) : null}
              {!loading && items.length === 0 ? (
                <div className="standard-catalog-picker__list-empty">
                  No rows — adjust filters or clear search.
                </div>
              ) : null}
              {items.map((row) => {
                const sel = selected?.standardItemId === row.standardItemId;
                const dup = existingCatalogIds.has(row.standardItemId);
                const displayName = (row.nomenclature || row.standardItemId || "").trim() || "—";
                return (
                  <div
                    key={row.standardItemId}
                    role={dup ? undefined : "button"}
                    tabIndex={dup ? undefined : 0}
                    aria-label={dup ? `${displayName}, already on resource` : `Select ${displayName}`}
                    title={dup ? "Already on this resource" : "Select this line"}
                    className={[
                      "standard-catalog-picker__list-row",
                      sel ? "standard-catalog-picker__list-row--selected" : "",
                      dup ? "standard-catalog-picker__list-row--disabled" : "standard-catalog-picker__list-row--clickable",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (!dup) setSelected(row);
                    }}
                    onKeyDown={(e) => {
                      if (dup) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(row);
                      }
                    }}
                  >
                    <span className="standard-catalog-picker__item-name">
                      {displayName}
                      {dup ? (
                        <span className="text-muted standard-catalog-picker__dup"> (added)</span>
                      ) : null}
                    </span>
                    <span className="standard-catalog-picker__item-uom">{row.uom ?? "—"}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {selected ? (
            <div className="standard-catalog-picker__selection">
              <span className="standard-catalog-picker__selection-label">Selected</span>
              <span className="standard-catalog-picker__selection-text">
                {(selected.nomenclature || selected.standardItemId || "").trim() || "—"}
                <span className="standard-catalog-picker__selection-uom">
                  {" "}
                  · {selected.uom?.trim() || "—"}
                </span>
                <span className="standard-catalog-picker__selection-id text-muted">
                  {" "}
                  ({selected.standardItemId})
                </span>
              </span>
            </div>
          ) : (
            <p className="standard-catalog-picker__selection standard-catalog-picker__selection--empty text-muted">
              Click a row in the list to select a line.
            </p>
          )}

          <div className="standard-catalog-picker__actions">
            <div className="standard-catalog-picker__actions-start">
              <button
                type="button"
                className="btn btn--tg-secondary btn--sm"
                disabled={loading || offset + items.length >= total}
                onClick={() => void loadItems(offset + items.length, true)}
              >
                Load more
              </button>
              {!loading && total > 0 ? (
                <span className="standard-catalog-picker__count text-muted">
                  Showing {items.length} of {total}
                </span>
              ) : null}
            </div>
            <div className="standard-catalog-picker__actions-end">
              <div className="standard-catalog-picker__qty">
                <label htmlFor={`sim-qty-${resource.id}`}>Qty</label>
                <input
                  id={`sim-qty-${resource.id}`}
                  type="number"
                  min={0}
                  step={1}
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn--primary btn--tg btn--sm standard-catalog-picker__add-btn"
                disabled={
                  !selected ||
                  existingCatalogIds.has(selected.standardItemId) ||
                  !(selected.nomenclature || selected.standardItemId).trim()
                }
                onClick={() => {
                  if (!selected) return;
                  const label = (selected.nomenclature || selected.standardItemId).trim();
                  const unit = (selected.uom || "NOS").trim();
                  const quantity = Math.max(0, Math.floor(Number(addQty)) || 0);
                  void (async () => {
                    const suggestedHsn = (selected.suggestedHsn4 ?? "").trim().replace(/\s+/g, "").slice(0, 40);
                    const ok = await addStandardResourceItem(resource.id, {
                      label,
                      quantity,
                      unit,
                      hsnCode: suggestedHsn,
                      catalogStandardItemId: selected.standardItemId,
                    });
                    if (ok) setSelected(null);
                  })();
                }}
              >
                Add to resource
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
